import { useEffect, useRef } from 'react';

import { send } from '@actual-app/core/platform/client/connection';
import { buildSnapshot } from '@actual-app/core/shared/mtxbudget';
import type { Snapshot } from '@actual-app/core/shared/mtxbudget';

import { useMtxbudget } from './useMtxbudget';

/**
 * Hands the current plan to the server whenever it changes, so that the
 * Monday message, shortfall alerts and the calendar feed stay accurate
 * without the server ever opening the budget.
 */
export function MtxSnapshotPusher() {
  const { config, plan, occurrences, today, isLoading, toComplete } =
    useMtxbudget();
  const lastPushed = useRef('');
  // The list is rebuilt on every render: the push follows its content only.
  const completeKey = JSON.stringify(
    toComplete.map(item =>
      item.kind === 'amount'
        ? { id: item.id, name: item.name, date: item.date }
        : { id: item.id, name: item.name },
    ),
  );

  useEffect(() => {
    if (!config || isLoading || plan.length === 0) {
      return;
    }
    const snapshot = buildSnapshot({
      plan,
      occurrences,
      today,
      generatedAt: new Date().toISOString(),
      // Each amount the banks do not send is announced once, as it appears.
      toComplete: JSON.parse(completeKey) as Snapshot['toComplete'],
    });
    const { generatedAt: _generatedAt, ...content } = snapshot;
    const fingerprint = JSON.stringify(content);
    if (fingerprint === lastPushed.current) {
      return;
    }
    // Schedules arrive in several live-query updates: wait for them to settle.
    const timer = setTimeout(() => {
      lastPushed.current = fingerprint;
      void send('mtxbudget-server', { route: 'snapshot', body: { snapshot } });
    }, 4000);
    return () => clearTimeout(timer);
  }, [config, plan, occurrences, today, isLoading, completeKey]);

  return null;
}
